import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { publicGet } from "@/lib/publicApi";
import MarketingShell from "@/components/MarketingShell";

/** Minimal but faithful Markdown → HTML for CMS pages.
 *  Handles: headings (##/###), bold, links, lists, inline anchors, paragraphs.
 *  Deliberately conservative — CMS content is trusted (super_admin only). */
function renderMd(md) {
    if (!md) return "";
    // Escape only < / > so raw HTML anchors we injected (`<a id="...">`) still work.
    let s = md.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // Restore intentional anchor tags we allow.
    s = s.replace(/&lt;a id=&quot;([\w-]+)&quot;&gt;&lt;\/a&gt;/g, '<a id="$1"></a>');
    s = s.replace(/&lt;a id="([\w-]+)"&gt;&lt;\/a&gt;/g, '<a id="$1"></a>');
    // Headings
    s = s.replace(/^### (.*)$/gm, '<h3 class="text-lg font-bold text-[#123B5D] mt-6 mb-2">$1</h3>');
    s = s.replace(/^## (.*)$/gm, '<h2 class="text-2xl font-bold text-[#123B5D] mt-8 mb-3">$1</h2>');
    // Links & bold & code
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a class="text-[#0F766E] underline" href="$2">$1</a>');
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/`([^`]+)`/g, '<code class="text-xs bg-[#F5F8FA] px-1 py-0.5 rounded">$1</code>');
    // Lists
    s = s.replace(/(^|\n)- (.*)/g, '$1<li class="ml-6 list-disc">$2</li>');
    s = s.replace(/(<li[^>]*>[\s\S]+?<\/li>\n?)+/g, m => `<ul class="my-2 space-y-1">${m}</ul>`);
    // Paragraphs (double newline)
    s = s.split(/\n{2,}/).map(chunk => {
        if (/^<(h\d|ul|ol|div|section)/.test(chunk.trim())) return chunk;
        return `<p class="text-[#17212B]/85 leading-relaxed my-3">${chunk.trim()}</p>`;
    }).join("\n");
    return s;
}

export default function CmsPage({ slugOverride }) {
    const params = useParams();
    const slug = slugOverride || params.slug;
    const [page, setPage] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        setPage(null); setErr("");
        publicGet(`/pages/${encodeURIComponent(slug)}`)
            .then(setPage)
            .catch(e => setErr(e?.data?.detail || "Page not found"));
    }, [slug]);

    useEffect(() => {
        if (page?.title) {
            document.title = `${page.title} | Doctorsprofile`;
            const m = document.querySelector('meta[name="description"]');
            if (m && page.meta_description) m.setAttribute("content", page.meta_description);
        }
    }, [page]);

    return (
        <MarketingShell>
            <div className="max-w-3xl mx-auto py-14 px-6" data-testid={`cms-page-${slug}`}>
                {err && (
                    <div className="text-center py-20">
                        <h1 className="text-3xl font-bold text-[#123B5D]">Page not found</h1>
                        <p className="mt-2 text-[#17212B]/60">{err}</p>
                        <Link to="/" className="mt-4 inline-block text-[#0F766E] underline">Back to home</Link>
                    </div>
                )}
                {page && (
                    <article>
                        <div className="text-xs uppercase tracking-wider text-[#17212B]/60 mb-2">{page.category}</div>
                        <h1 className="text-4xl sm:text-5xl font-extrabold text-[#123B5D] leading-tight">{page.title}</h1>
                        {page.meta_description && (
                            <p className="mt-3 text-lg text-[#17212B]/70">{page.meta_description}</p>
                        )}
                        <hr className="my-6 border-[#123B5D]/10" />
                        <div className="prose-like" dangerouslySetInnerHTML={{ __html: renderMd(page.body_markdown) }} />
                    </article>
                )}
            </div>
        </MarketingShell>
    );
}
